'use client';

import { Fragment, useEffect, useState } from 'react';
import type { Booking, RentalClient } from '@/types';
import { buildWhatsAppLink } from '@/lib/morocco';
import { formatMoney } from '@/lib/currency';
import { api, ApiError } from '@/lib/api';
import { parseBookingPage } from '@/lib/booking/laravel-mirror';
import { parseClientPage } from '@/lib/clients/client-catalog';
import { useDeskSummary } from '@/lib/desk/summary';
import { listPath } from '@/lib/query/page';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useDebouncedSearch, useSectionParams } from '@/lib/query/table-params';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { Plus, Pencil, Trash2, MessageCircle, Flag, FlagOff, Download, ChevronDown } from 'lucide-react';
import { DeskTable } from './DeskTable';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { SlideOver } from './SlideOver';
import { StatusChip, riskTone, useDeskToast } from './desk-feedback';
import { RecordPager } from './RecordPager';

interface Props {
  currency?: string | null;
  canDelete?: boolean;
  onRetry?: () => void;
  requestCreate?: number;
}

type RiskFilter = 'all' | RentalClient['riskLevel'];

interface FormState {
  name: string;
  phone: string;
  email: string;
  licenseNumber: string;
  cinPassport: string;
  address: string;
  riskLevel: RentalClient['riskLevel'];
  riskNote: string;
}

const EMPTY: FormState = {
  name: '', phone: '', email: '', licenseNumber: '', cinPassport: '', address: '', riskLevel: 'clean', riskNote: '',
};

const inputCls =
  'w-full rounded-2xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-[#FF8A00]';
const labelCls = 'mb-1 block text-xs font-semibold text-muted';

const CLIENT_COLUMNS = [
  { id: 'client', label: 'Client', sortable: true },
  { id: 'contact', label: 'Contact' },
  { id: 'documents', label: 'Documents' },
  { id: 'rentals', label: 'Rentals' },
  { id: 'risk', label: 'Risk' },
];

export default function ClientsModule({ currency, canDelete = false, onRetry, requestCreate = 0 }: Props) {
  const cur = currency ?? 'MAD';
  const table = useSectionParams('clients');
  const search = useDebouncedSearch('clients', table.search, table.write);
  const riskFilter = (table.read('risk', 'all') || 'all') as RiskFilter;
  const limits = useDeskSummary();
  const invalidate = useInvalidateTenantResource();
  const clientQuery = useTenantQuery(
    ['clients', table.page, table.search, table.sort, table.direction, riskFilter],
    listPath('/api/clients', {
      page: table.page,
      perPage: 20,
      sort: table.sort || 'created_at',
      direction: table.direction || 'desc',
      q: table.search,
      risk: riskFilter,
    }),
    parseClientPage,
  );
  const clients = (clientQuery.data?.data ?? []) as RentalClient[];
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'phone' | 'license', string>>>({});
  const [saving, setSaving] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<RentalClient | null>(null);
  useEffect(() => {
    if (!requestCreate) return;
    setEditingId(null);
    setForm(EMPTY);
    setFieldErrors({});
    setShowModal(true);
  }, [requestCreate]);
  const notify = useDeskToast();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const total = clientQuery.data?.meta.total ?? 0;
  const filtersActive = table.search !== '' || riskFilter !== 'all';
  const listKind = clientQuery.isLoading ? 'loading' as const
    : clientQuery.isError && !clientQuery.data ? 'error' as const
    : total === 0 && filtersActive ? 'filtered' as const
    : total === 0 ? 'empty' as const
    : 'ready' as const;
  const visibleIds = clients.map((client) => client.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleVisibleClients() {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function toggleClient(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const stats = {
    total: limits.data?.clients ?? total,
    blacklisted: limits.data?.summary.clientRisk.blacklisted ?? 0,
    vip: limits.data?.summary.clientRisk.vip ?? 0,
  };
  const [linked, setLinked] = useState<Record<string, Booking[]>>({});

  async function loadLinked(client: RentalClient) {
    if (linked[client.id]) return;
    const page = parseBookingPage(await api<unknown>(listPath('/api/bookings', { q: client.phone, perPage: 20 })));
    setLinked((current) => ({ ...current, [client.id]: page.data as Booking[] }));
  }

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY);
    setFieldErrors({});
    setShowModal(true);
  };

  const openEdit = (c: RentalClient) => {
    setEditingId(c.id);
    setForm({
      name: c.name, phone: c.phone, email: c.email, licenseNumber: c.licenseNumber,
      cinPassport: c.cinPassport ?? '', address: c.address ?? '',
      riskLevel: c.riskLevel, riskNote: c.riskNote ?? '',
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const handleSave = async () => {
    const nextErrors: Partial<Record<'name' | 'phone' | 'license', string>> = {};
    if (!form.name.trim()) nextErrors.name = 'Enter the client name.';
    if (!form.phone.trim()) nextErrors.phone = 'Enter a phone number.';
    if (!form.licenseNumber.trim()) nextErrors.license = 'Enter the license number.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSaving(true);
    const body = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      licenseNumber: form.licenseNumber.trim(),
      cinPassport: form.cinPassport.trim(),
      address: form.address.trim(),
      riskLevel: form.riskLevel,
      riskNote: form.riskNote.trim(),
    };
    try {
      if (editingId) {
        await api(`/api/clients/${editingId}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await api('/api/clients', { method: 'POST', body: JSON.stringify(body) });
      }
      await invalidate('clients');
      await invalidate('desk-summary');
      setShowModal(false);
      notify(editingId ? 'Client updated.' : 'Client saved.');
    } catch (error: unknown) {
      notify(error instanceof ApiError ? error.message : 'Could not save this client.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client: RentalClient) => {
    try {
      await api(`/api/clients/${client.id}`, { method: 'DELETE' });
      await invalidate('clients');
      await invalidate('desk-summary');
      notify('Client removed.');
    } catch (error: unknown) {
      notify(error instanceof ApiError ? error.message : 'Could not remove this client.', 'error');
    } finally {
      setClientToDelete(null);
    }
  };

  const toggleFlag = async (client: RentalClient) => {
    const next: RentalClient['riskLevel'] = client.riskLevel === 'blacklisted' ? 'clean' : 'blacklisted';
    try {
      await api(`/api/clients/${client.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: client.name,
          phone: client.phone,
          email: client.email,
          licenseNumber: client.licenseNumber,
          cinPassport: client.cinPassport ?? '',
          address: client.address ?? '',
          riskLevel: next,
          riskNote: client.riskNote ?? '',
        }),
      });
      await invalidate('clients');
      await invalidate('desk-summary');
    } catch (error: unknown) {
      notify(error instanceof ApiError ? error.message : 'Could not update this client.', 'error');
    }
  };

  const exportCSV = () => {
    const header = 'id,name,phone,email,license,cin,rentals,totalSpent,risk\n';
    const rows = clients.map((c) =>
      [c.id, `"${c.name}"`, c.phone, c.email, c.licenseNumber, c.cinPassport ?? '', c.rentalsCount, c.totalSpent, c.riskLevel].join(',')
    );
    const blob = new Blob([header + rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'clients.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        {[
          { label: 'Total clients', value: stats.total },
          { label: 'Blacklisted', value: stats.blacklisted },
          { label: 'VIP (10+ rentals)', value: stats.vip },
        ].map((s) => (
          <div key={s.label} className="rounded-3xl border border-line bg-card p-3">
            <p className="text-muted">{s.label}</p>
            <p className="text-lg font-bold text-ink">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button onClick={exportCSV} className="flex items-center gap-1 rounded-2xl border border-line bg-card px-3 py-2 text-xs font-bold text-ink">
          <Download size={13} /> CSV
        </button>
        <button onClick={openAdd} className="flex items-center gap-1 rounded-2xl bg-brand px-4 py-2 text-xs font-bold text-slate-950">
          <Plus size={14} /> Add client
        </button>
      </div>

      <DeskTable
        storageKey="fleetava-desk-client-columns"
        columns={CLIENT_COLUMNS}
        filters={
          <div className="flex flex-wrap gap-2">
            <select value={riskFilter} onChange={(event) => table.write({ risk: event.target.value })} aria-label="Risk" className={`${inputCls} w-auto`}>
              <option value="all">All risks</option>
              <option value="clean">clean</option>
              <option value="watch">watch</option>
              <option value="blacklisted">blacklisted</option>
            </select>
          </div>
        }
        selectedCount={selected.size}
        allVisibleSelected={allVisibleSelected}
        onToggleVisible={toggleVisibleClients}
        onClearSelection={() => setSelected(new Set())}
        search={<input value={search.draft} onChange={(event) => search.onSearch(event.target.value)} placeholder="Search name, phone, or license" aria-label="Search clients" className={`${inputCls} max-w-sm`} />}
        refreshing={clientQuery.isFetching && Boolean(clientQuery.data)}
        refreshError={clientQuery.isError && Boolean(clientQuery.data)}
        sort={table.sort === 'name' ? { id: 'client', direction: table.direction === 'asc' ? 'asc' : 'desc' } : undefined}
        onSort={() => table.write({ sort: 'name', dir: table.sort === 'name' && table.direction === 'asc' ? 'desc' : 'asc' })}
        listState={listKind}
        onRetry={() => { void clientQuery.refetch(); onRetry?.(); }}
        emptyTitle="No clients yet."
        filteredTitle="No rows match these filters."
        errorTitle="The client list could not be loaded."
        notice={null}
        onDismissNotice={() => undefined}
      >
        {(shown, columnCount) => clients.map((c) => {
              const linkedRows = linked[c.id] ?? [];
              const open = expandedId === c.id;
              return (
                <Fragment key={c.id}>
                  <tr className="border-t border-line">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleClient(c.id)} aria-label={`Select ${c.name}`} />
                    </td>
                    {shown('client') && <td className="px-3 py-2">
                      <p className="font-bold">{c.name}</p>
                      <p className="font-mono text-[11px] text-muted">{c.id}</p>
                    </td>}
                    {shown('contact') && <td className="px-3 py-2">
                      <p>{c.phone}</p>
                      <p className="truncate text-muted">{c.email || '—'}</p>
                      <a href={buildWhatsAppLink(c.phone, `Bonjour ${c.name}, — fleetava`)} target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 rounded-lg bg-success px-2 py-1 font-bold text-white">
                        <MessageCircle size={11} /> WhatsApp
                      </a>
                    </td>}
                    {shown('documents') && <td className="px-3 py-2">
                      <p>Lic: {c.licenseNumber}</p>
                      <p className="text-muted">{c.cinPassport || 'CIN: —'}</p>
                    </td>}
                    {shown('rentals') && <td className="px-3 py-2">
                      <p className="font-bold text-ink">{c.rentalsCount} rentals</p>
                      <p>{formatMoney(c.totalSpent, cur)}</p>
                    </td>}
                    {shown('risk') && <td className="px-3 py-2">
                      <StatusChip tone={riskTone(c.riskLevel)}>{c.riskLevel}</StatusChip>
                      <button onClick={() => toggleFlag(c)} title={c.riskLevel === 'blacklisted' ? 'Unflag' : 'Blacklist'}
                        className="ml-1 rounded-lg border border-line p-1.5 text-muted hover:bg-shell">
                        {c.riskLevel === 'blacklisted' ? <FlagOff size={12} /> : <Flag size={12} />}
                      </button>
                    </td>}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(c)} className="rounded-lg border border-line p-1.5 text-muted"><Pencil size={12} /></button>
                        {canDelete && <button type="button" onClick={() => setClientToDelete(c)} className="rounded-lg border border-danger/40 p-1.5 text-danger" aria-label={`Delete ${c.name}`}><Trash2 size={12} /></button>}
                        <button onClick={() => { const next = open ? null : c.id; setExpandedId(next); if (next) void loadLinked(c); }} className="rounded-lg border border-line p-1.5 text-muted">
                          <ChevronDown size={12} className={open ? 'rotate-180' : ''} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-t border-line bg-shell">
                      <td colSpan={columnCount} className="px-3 py-2">
                        <p className="mb-1 font-bold text-ink">Bookings ({linkedRows.length}){c.riskNote ? ` · Note: ${c.riskNote}` : ''}{c.address ? ` · ${c.address}` : ''}</p>
                        {linkedRows.length === 0 && <p className="text-muted">No linked bookings.</p>}
                        <div className="space-y-1">
                          {linkedRows.map((b) => (
                            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-line bg-card px-2.5 py-1.5">
                              <span className="font-mono font-bold text-ink">{b.id}</span>
                              <span className="text-muted">{b.vehicleName}</span>
                              <span className="text-muted">{b.pickupDate} → {b.dropoffDate}</span>
                              <span className="rounded-full bg-shell px-2 py-0.5 font-bold text-muted">{b.status}</span>
                              <span className="font-bold text-ink">{formatMoney(b.totalPrice, cur)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
      </DeskTable>
      <RecordPager
        page={clientQuery.data?.meta.page ?? table.page}
        pages={Math.max(1, Math.ceil(total / (clientQuery.data?.meta.perPage || 20)))}
        total={total}
        onPage={(next) => table.write({ page: String(next) }, false)}
      />

      {/* Add/Edit modal */}
      <SlideOver
        open={showModal}
        title={editingId ? 'Edit client' : 'New client'}
        onClose={() => { if (!saving) setShowModal(false); }}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowModal(false)} disabled={saving} className="rounded-2xl border border-line px-4 py-2 text-xs font-bold text-ink disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-2xl bg-brand px-4 py-2 text-xs font-bold text-slate-950 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        }
      >
            <div className="grid gap-2 sm:grid-cols-2">
              <div><label className={labelCls}>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />{fieldErrors.name && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.name}</p>}</div>
              <div><label className={labelCls}>Phone *</label><input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} />{fieldErrors.phone && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.phone}</p>}</div>
              <div><label className={labelCls}>Email</label><input value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>License number *</label><input value={form.licenseNumber} onChange={(e) => set('licenseNumber', e.target.value)} className={inputCls} />{fieldErrors.license && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.license}</p>}</div>
              <div><label className={labelCls}>CIN / Passport</label><input value={form.cinPassport} onChange={(e) => set('cinPassport', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Address</label><input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Risk level</label>
                <select value={form.riskLevel} onChange={(e) => set('riskLevel', e.target.value as FormState['riskLevel'])} className={inputCls}>
                  <option value="clean">clean</option><option value="watch">watch</option><option value="blacklisted">blacklisted</option>
                </select></div>
              <div><label className={labelCls}>Risk note</label><input value={form.riskNote} onChange={(e) => set('riskNote', e.target.value)} className={inputCls} /></div>
            </div>
      </SlideOver>
      <ConfirmDialog
        open={clientToDelete !== null}
        title={clientToDelete ? `Delete ${clientToDelete.name}?` : 'Delete client?'}
        description="The client leaves this agency list."
        confirmLabel="Delete client"
        onCancel={() => setClientToDelete(null)}
        onConfirm={() => {
          if (clientToDelete) handleDelete(clientToDelete);
        }}
      />
    </div>
  );
}
